// @contexts/PageCounterClient.context.tsx
import React, { createContext, useContext, useMemo, type ReactNode } from "react";
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import { requireContext } from "@contexts/requireContext";
import { DefaultPageCounterClient } from "@services/clients/DefaultPageCounterClient";
import { StubPageCounterClient } from "@services/stubs/StubPageCounterClient";
import type { PageCounterClient } from "@services/clients/PageCounterClient";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";

const defaultStubPageCounterClient = new StubPageCounterClient();

const PageCounterClientContext = createContext<OptionTs<PageCounterClient>>(Option.none());

type PageCounterClientProviderProps =
    {
        readonly children: ReactNode;
        readonly client: OptionTs<PageCounterClient>;
    };

export const PageCounterClientProvider: React.FC<PageCounterClientProviderProps> = (props) => {
    const client = useMemo<PageCounterClient>(
        () =>
            Option.fold(
                props.client,
                () => (enabledFeatures[FeaturesEnum.StubClients] ? defaultStubPageCounterClient : new DefaultPageCounterClient()),
                (value) => value,
            ),
        [props.client],
    );
    const contextValue = useMemo(() => Option.some(client), [client]);

    return <PageCounterClientContext.Provider value={contextValue}>{props.children}</PageCounterClientContext.Provider>;
};

export const usePageCounterClient = (): PageCounterClient =>
    requireContext(useContext(PageCounterClientContext), "usePageCounterClient must be used within a PageCounterClientProvider");
